import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Loading from '../../../components/Loading';
import MessageOdoFooterButton from '../../../components/patient/MessageOdoFooterButton';
import { useDragDrop } from '../../../hooks/useDragDrop';
import { useLayoutFooter, useStepperContext } from '../../../layouts/PatientCreationLayout';
import { useSubmissionForm } from '../../../providers/patient/PatientSubmissionContext';
import { useToast } from '../../../providers/ToastProvider';
import {
    deleteFile,
    getDownloadUrl,
    getFiles,
    uploadFile,
    type PatientFile
} from '../../../services/cloud-files';
import { reportCaughtError } from '../../../services/errorReporting';
import { updatePatientCreation } from '../../../services/patient/creation';

// Reference images
import frontBuccalRef from '../../../assets/images/front_buccal.jpg';
import rightBuccalRef from '../../../assets/images/right_buccal.jpg';
import leftBuccalRef from '../../../assets/images/left_buccal.jpg';
import upperOcclusalRef from '../../../assets/images/upper_occlusal.jpg';
import lowerOcclusalRef from '../../../assets/images/lower_occlusal.jpg';
import profileRightRef from '../../../assets/images/right_profile.jpg';
import profileLeftRef from '../../../assets/images/left_profile.jpg';
import frontalRestRef from '../../../assets/images/frontal_rest.jpg';
import frontalSmileRef from '../../../assets/images/frontal_smile.jpg';

// Icons
import xMark from '../../../assets/icons/x-mark.svg';

interface PhotoSlot {
    id: string;
    label: string;
    referenceImage: string;
    file: File | null;
    preview: string | null;
    existingFile: PatientFile | null;
    isRequired: boolean;
}

interface OtherPhoto {
    id: string;
    file: File | null;
    preview: string | null;
    existingFile: PatientFile | null;
}

const MANDATORY_PHOTO_TYPES: Omit<PhotoSlot, 'file' | 'preview' | 'existingFile'>[] = [
    {
        id: 'front_buccal',
        label: 'Front Buccal',
        referenceImage: frontBuccalRef,
        isRequired: false
    },
    {
        id: 'right_buccal',
        label: 'Right Buccal',
        referenceImage: rightBuccalRef,
        isRequired: false
    },
    { id: 'left_buccal', label: 'Left Buccal', referenceImage: leftBuccalRef, isRequired: false },
    {
        id: 'upper_occlusal',
        label: 'Upper Occlusal',
        referenceImage: upperOcclusalRef,
        isRequired: false
    },
    {
        id: 'lower_occlusal',
        label: 'Lower Occlusal',
        referenceImage: lowerOcclusalRef,
        isRequired: false
    },
    {
        id: 'profile_right',
        label: 'Profile Right',
        referenceImage: profileRightRef,
        isRequired: false
    },
    {
        id: 'profile_left',
        label: 'Profile Left',
        referenceImage: profileLeftRef,
        isRequired: false
    },
    {
        id: 'frontal_rest',
        label: 'Frontal Rest',
        referenceImage: frontalRestRef,
        isRequired: false
    },
    {
        id: 'frontal_smile',
        label: 'Frontal Smile',
        referenceImage: frontalSmileRef,
        isRequired: false
    }
];

const UploadPatientPhotos: React.FC = () => {
    const { formData, updateSubmissionForm } = useSubmissionForm();
    const { goNext } = useStepperContext();
    const { setFooterContent } = useLayoutFooter();
    const { showToast } = useToast();

    const [photoSlots, setPhotoSlots] = useState<PhotoSlot[]>(
        MANDATORY_PHOTO_TYPES.map((type) => ({
            ...type,
            file: null,
            preview: null,
            existingFile: null
        }))
    );
    const [otherPhotos, setOtherPhotos] = useState<OtherPhoto[]>([]);
    const [photoDate, setPhotoDate] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
    const [isDateModified, setIsDateModified] = useState(false);

    // Upload progress state
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
    const [totalUploadCount, setTotalUploadCount] = useState(0);
    const [currentFileName, setCurrentFileName] = useState('');
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);

    const otherPhotoInputRef = useRef<HTMLInputElement>(null);
    const photoSlotsRef = useRef(photoSlots);
    const otherPhotosRef = useRef(otherPhotos);

    // Keep refs in sync for unmount cleanup
    photoSlotsRef.current = photoSlots;
    otherPhotosRef.current = otherPhotos;

    // Load existing photos from GCS
    const loadExistingPhotos = useCallback(async () => {
        if (!formData?.id) return;

        setIsLoadingFiles(true);
        try {
            const [photos] = await Promise.all([
                getFiles(formData.id, 'PHOTO'),
                getFiles(formData.id, 'PROFILE_PICTURE')
            ]);

            // Map existing photos to slots based on metadata and fetch download URLs
            const updatedSlots = await Promise.all(
                MANDATORY_PHOTO_TYPES.map(async (slotType) => {
                    const existingPhoto = photos.find((p) => p.metadata?.photoType === slotType.id);
                    if (existingPhoto) {
                        try {
                            const downloadUrl = await getDownloadUrl(
                                formData.id as string,
                                existingPhoto.id
                            );
                            return {
                                ...slotType,
                                file: null,
                                preview: downloadUrl,
                                existingFile: existingPhoto
                            };
                        } catch {
                            // Download URL fetch failed; slot will show without preview
                        }
                    }
                    return {
                        ...slotType,
                        file: null,
                        preview: null,
                        existingFile: null
                    };
                })
            );
            setPhotoSlots(updatedSlots);

            // Load other photos (those without a known photoType or with photoType 'other')
            const otherExisting = photos.filter(
                (p) =>
                    !p.metadata?.photoType ||
                    p.metadata.photoType === 'other' ||
                    !MANDATORY_PHOTO_TYPES.some((t) => t.id === p.metadata?.photoType)
            );

            // Fetch download URLs for other photos
            const otherWithUrls = await Promise.all(
                otherExisting.map(async (p) => {
                    try {
                        const downloadUrl = await getDownloadUrl(formData.id as string, p.id);
                        return {
                            id: p.id,
                            file: null,
                            preview: downloadUrl,
                            existingFile: p
                        };
                    } catch {
                        return {
                            id: p.id,
                            file: null,
                            preview: null,
                            existingFile: p
                        };
                    }
                })
            );
            setOtherPhotos(otherWithUrls);
        } catch {
            // Failed to load existing photos; slots will show as empty
        } finally {
            setIsLoadingFiles(false);
        }
    }, [formData?.id]);

    useEffect(() => {
        if (formData?.id) {
            loadExistingPhotos();
        }
    }, [formData?.id, loadExistingPhotos]);

    useEffect(() => {
        if (formData?.photosDate) {
            setPhotoDate(formData.photosDate);
        }
    }, [formData?.photosDate]);

    const handleSlotFileSelect = useCallback((slotId: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPhotoSlots((prev) =>
            prev.map((slot) => {
                if (slot.id === slotId) {
                    // Revoke old preview if exists
                    if (slot.preview) {
                        URL.revokeObjectURL(slot.preview);
                    }
                    return { ...slot, file, preview, existingFile: null };
                }
                return slot;
            })
        );
    }, []);

    const handleSlotRemove = useCallback(
        async (slotId: string) => {
            const slot = photoSlots.find((s) => s.id === slotId);
            if (!slot) return;

            // If there's an existing file, delete it from GCS
            if (slot.existingFile && formData?.id) {
                try {
                    await deleteFile(formData.id, slot.existingFile.id);
                } catch {
                    // Delete failed; photo will remain
                }
            }

            // Revoke preview URL if exists
            if (slot.preview) {
                URL.revokeObjectURL(slot.preview);
            }

            setPhotoSlots((prev) =>
                prev.map((s) => {
                    if (s.id === slotId) {
                        return { ...s, file: null, preview: null, existingFile: null };
                    }
                    return s;
                })
            );
        },
        [photoSlots, formData?.id]
    );

    const handleOtherPhotoAdd = useCallback((files: FileList | File[]) => {
        const newPhotos: OtherPhoto[] = Array.from(files)
            .filter((file) => file.type.startsWith('image/'))
            .map((file) => ({
                id: crypto.randomUUID(),
                file,
                preview: URL.createObjectURL(file),
                existingFile: null
            }));

        if (newPhotos.length > 0) {
            setOtherPhotos((prev) => [...prev, ...newPhotos]);
        }
    }, []);

    const { isDragging: isOtherDragging, dragHandlers: otherDragHandlers } = useDragDrop({
        onDrop: handleOtherPhotoAdd
    });

    const handleOtherPhotoRemove = useCallback(
        async (photoId: string) => {
            const photo = otherPhotos.find((p) => p.id === photoId);
            if (!photo) return;

            // If there's an existing file, delete it from GCS
            if (photo.existingFile && formData?.id) {
                try {
                    await deleteFile(formData.id, photo.existingFile.id);
                } catch {
                    // Delete failed; photo will remain
                }
            }

            // Revoke preview URL if exists
            if (photo.preview) {
                URL.revokeObjectURL(photo.preview);
            }

            setOtherPhotos((prev) => prev.filter((p) => p.id !== photoId));
        },
        [otherPhotos, formData?.id]
    );

    const handleNextClick = useCallback(async () => {
        setIsLoading(true);

        // Collect all photos to upload
        const photosToUpload: { file: File; photoType: string; isProfilePicture: boolean }[] = [];

        // Add mandatory photos with new files
        for (const slot of photoSlots) {
            if (slot.file) {
                photosToUpload.push({
                    file: slot.file,
                    photoType: slot.id,
                    isProfilePicture: slot.id === 'frontal_smile'
                });
            }
        }

        // Add other photos with new files
        for (const photo of otherPhotos) {
            if (photo.file) {
                photosToUpload.push({
                    file: photo.file,
                    photoType: 'other',
                    isProfilePicture: false
                });
            }
        }

        if (photosToUpload.length > 0) {
            setIsUploading(true);
            setUploadErrors([]);
            // Count total uploads (frontal_smile counts as 2)
            const totalUploads = photosToUpload.reduce(
                (acc, p) => acc + (p.isProfilePicture ? 2 : 1),
                0
            );
            setTotalUploadCount(totalUploads);
            let uploadIndex = 0;
            const errors: string[] = [];

            for (const photo of photosToUpload) {
                uploadIndex++;
                setCurrentUploadIndex(uploadIndex);
                setCurrentFileName(photo.file.name);
                setUploadProgress(0);

                try {
                    // Upload as PHOTO
                    await uploadFile(
                        formData?.id as string,
                        photo.file,
                        'PHOTO',
                        `${photo.photoType} photo`,
                        { photoType: photo.photoType },
                        (progress) => setUploadProgress(progress)
                    );

                    // If it's frontal_smile, also upload as PROFILE_PICTURE and store in form for Submit case page
                    if (photo.isProfilePicture) {
                        uploadIndex++;
                        setCurrentUploadIndex(uploadIndex);
                        setCurrentFileName(`${photo.file.name} (Profile)`);
                        setUploadProgress(0);

                        await uploadFile(
                            formData?.id as string,
                            photo.file,
                            'PROFILE_PICTURE',
                            'Profile picture',
                            { photoType: 'frontal_smile' },
                            (progress) => setUploadProgress(progress)
                        );

                        // Revoke previous blob URL to prevent memory leak
                        if (formData?.profilePicture?.preview) {
                            URL.revokeObjectURL(formData.profilePicture.preview);
                        }
                        const newPreview = URL.createObjectURL(photo.file);
                        updateSubmissionForm({
                            profilePicture: {
                                id: 'profile',
                                file: photo.file,
                                preview: newPreview,
                                type: 'frontal_smile',
                                name: photo.file.name
                            }
                        });
                    }
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Upload failed';
                    errors.push(`Failed to upload ${photo.file.name}: ${message}`);
                }
            }

            setIsUploading(false);
            setUploadProgress(0);
            setCurrentUploadIndex(0);
            setTotalUploadCount(0);
            setCurrentFileName('');

            if (errors.length > 0) {
                setUploadErrors(errors);
                setIsLoading(false);
                return;
            }
        }

        // Handle date update
        if (isDateModified && photoDate && formData?.id) {
            try {
                await updatePatientCreation(formData.id, { photosDate: photoDate });
                updateSubmissionForm({ photosDate: photoDate });
            } catch (error) {
                showToast('Failed to save photo date. Please try again.', 'error');
                reportCaughtError(error);
                setIsLoading(false);
                return;
            }
        }

        setIsLoading(false);
        goNext();
    }, [
        photoSlots,
        otherPhotos,
        formData?.id,
        isDateModified,
        photoDate,
        updateSubmissionForm,
        goNext
    ]);

    // Check if all required photos are uploaded
    const allRequiredUploaded = photoSlots
        .filter((slot) => slot.isRequired)
        .every((slot) => slot.file || slot.existingFile);

    useEffect(() => {
        const isButtonDisabled = isLoading || isUploading || !allRequiredUploaded;

        setFooterContent(
            <div className="w-full flex justify-end items-center gap-3">
                <MessageOdoFooterButton />
                <button
                    disabled={isButtonDisabled}
                    onClick={handleNextClick}
                    className={`flex flex-row justify-center items-center px-4 py-3.5 gap-2 min-w-32 h-10 rounded-lg font-bold text-sm leading-4 text-center tracking-wider ${
                        isButtonDisabled
                            ? 'bg-gray-300 text-white cursor-not-allowed'
                            : 'bg-gray-800 text-white hover:bg-gray-700'
                    }`}
                >
                    {isUploading ? (
                        <span className="flex items-center gap-1">
                            Uploading
                            <span className="flex gap-0.5">
                                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                                    .
                                </span>
                                <span
                                    className="animate-bounce"
                                    style={{ animationDelay: '150ms' }}
                                >
                                    .
                                </span>
                                <span
                                    className="animate-bounce"
                                    style={{ animationDelay: '300ms' }}
                                >
                                    .
                                </span>
                            </span>
                        </span>
                    ) : (
                        'Continue'
                    )}
                </button>
            </div>
        );

        return () => {
            setFooterContent(null);
        };
    }, [setFooterContent, isLoading, isUploading, handleNextClick, allRequiredUploaded]);

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            photoSlotsRef.current.forEach((slot) => {
                if (slot.preview) URL.revokeObjectURL(slot.preview);
            });
            otherPhotosRef.current.forEach((photo) => {
                if (photo.preview) URL.revokeObjectURL(photo.preview);
            });
        };
    }, []);

    return (
        <>
            {isLoading && <Loading text="Saving..." />}
            {isLoadingFiles && <Loading text="Loading photos..." />}

            <div className="max-w-5xl mx-auto">
                <div className="flex flex-row justify-center items-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Upload Photos</h1>
                </div>

                <p className="text-gray-600 mb-6">
                    <span className="text-gray-900 underline italic font-bold">Important:</span> If
                    the patient has been previously treated, only submit images taken after their
                    most recent treatment.
                    {formData?.caseType && formData.caseType.monday_key === 'Pediatrics' && (
                        <span>
                            {' '}
                            If your patient has teeth erupting or you are aware of potential
                            shifting, please upload records no older than 4 weeks for an accurate
                            diagnosis and treatment plan.
                        </span>
                    )}
                </p>

                {/* Required Photos Grid */}
                <div className="mb-8">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Required Photos</h2>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                        {photoSlots.map((slot) => (
                            <PhotoSlotCard
                                key={slot.id}
                                slot={slot}
                                onFileSelect={(file) => handleSlotFileSelect(slot.id, file)}
                                onRemove={() => handleSlotRemove(slot.id)}
                            />
                        ))}
                    </div>
                </div>

                {/* Other Photos Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-gray-800">Other Photos (Optional)</h2>
                        <button
                            onClick={() => otherPhotoInputRef.current?.click()}
                            className="text-sm font-bold text-[#3773A8] hover:underline"
                        >
                            + Add Photos
                        </button>
                        <input
                            ref={otherPhotoInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                if (e.target.files) {
                                    handleOtherPhotoAdd(e.target.files);
                                    e.target.value = '';
                                }
                            }}
                        />
                    </div>

                    <div
                        className={`border-2 border-dashed rounded-xl p-4 transition-colors duration-200 ${
                            isOtherDragging
                                ? 'border-[#3773A8] bg-blue-50'
                                : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onClick={() =>
                            otherPhotos.length === 0 && otherPhotoInputRef.current?.click()
                        }
                        {...otherDragHandlers}
                    >
                        {otherPhotos.length > 0 ? (
                            <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
                                {otherPhotos.map((photo) => (
                                    <OtherPhotoCard
                                        key={photo.id}
                                        photo={photo}
                                        onRemove={() => handleOtherPhotoRemove(photo.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-6 cursor-pointer">
                                <svg
                                    className="w-8 h-8 text-gray-400 mb-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                <p className="text-sm text-gray-500">
                                    {isOtherDragging
                                        ? 'Drop photos here'
                                        : 'Click or drag photos here'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Date Input */}
                <div className="max-w-[35%]">
                    <div className="flex flex-col">
                        <label className="text-sm text-gray-800 leading-5 mb-2">
                            When were these photos taken?
                        </label>
                        <DatePicker
                            selected={
                                photoDate
                                    ? (() => {
                                          const [year, month, day] = photoDate
                                              .split('-')
                                              .map(Number);
                                          return new Date(year, month - 1, day);
                                      })()
                                    : null
                            }
                            onChange={(d: Date | null) => {
                                const iso = d
                                    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                                    : '';
                                setPhotoDate(iso);
                                setIsDateModified(true);
                            }}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="MM/dd/yyyy"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            wrapperClassName="w-full"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                        />
                    </div>
                </div>
            </div>

            {/* Upload Error Messages */}
            {uploadErrors.length > 0 && (
                <div className="max-w-5xl mx-auto mt-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="text-sm font-bold text-red-800 mb-2">
                            Some photos failed to upload
                        </h4>
                        <ul className="list-disc list-inside space-y-1">
                            {uploadErrors.map((err, i) => (
                                <li key={i} className="text-sm text-red-700">
                                    {err}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={() => setUploadErrors([])}
                            className="mt-3 text-sm font-medium text-red-600 hover:underline"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Upload Progress Modal */}
            {isUploading && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-96 shadow-xl">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Uploading Photos</h3>
                        <div className="mb-3">
                            <div className="flex items-center justify-between mb-1.5">
                                <p
                                    className="text-sm text-gray-600 truncate max-w-[70%]"
                                    title={currentFileName}
                                >
                                    {currentFileName}
                                </p>
                                <p className="text-sm text-gray-500 font-medium">
                                    {currentUploadIndex}/{totalUploadCount}
                                </p>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div
                                    className="bg-[#3773A8] h-3 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.round(uploadProgress)}%` }}
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1.5 text-right">
                                {Math.round(uploadProgress)}%
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Photo Slot Card Component
interface PhotoSlotCardProps {
    slot: PhotoSlot;
    onFileSelect: (file: File) => void;
    onRemove: () => void;
}

const PhotoSlotCard: React.FC<PhotoSlotCardProps> = ({ slot, onFileSelect, onRemove }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const hasPhoto = slot.file || slot.existingFile;

    const handleSlotDrop = useCallback(
        (files: File[]) => {
            if (hasPhoto) return;
            if (files[0]) {
                onFileSelect(files[0]);
            }
        },
        [hasPhoto, onFileSelect]
    );

    const { isDragging, dragHandlers } = useDragDrop({
        onDrop: handleSlotDrop,
        disabled: Boolean(hasPhoto)
    });

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            onFileSelect(file);
        }
        e.target.value = '';
    };

    const borderClass = hasPhoto
        ? 'border-green-500 bg-green-50'
        : isDragging
          ? 'border-[#3773A8] bg-blue-50 border-solid'
          : 'border-dashed border-gray-300 hover:border-gray-400 bg-gray-50';

    return (
        <div className="flex flex-col items-center">
            <div
                className={`relative w-full aspect-square rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${borderClass}`}
                onClick={() => !hasPhoto && inputRef.current?.click()}
                {...dragHandlers}
            >
                {hasPhoto ? (
                    <>
                        <img
                            src={slot.preview || slot.referenceImage}
                            alt={slot.label}
                            className="w-full h-full object-cover"
                        />
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                            className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                        >
                            <img src={xMark} alt="Remove" className="w-4 h-4" />
                        </button>
                        {slot.existingFile && (
                            <div className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-xs py-1 text-center">
                                Uploaded
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <img
                            src={slot.referenceImage}
                            alt={slot.label}
                            className="w-full h-full object-cover opacity-40"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-white/80 rounded-full p-2">
                                <svg
                                    className="w-6 h-6 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                            </div>
                        </div>
                    </>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                />
            </div>
            <p className="mt-2 text-xs text-gray-700 text-center font-medium">
                {slot.label}
                {slot.isRequired && <span className="text-red-500 ml-0.5">*</span>}
            </p>
        </div>
    );
};

// Other Photo Card Component
interface OtherPhotoCardProps {
    photo: OtherPhoto;
    onRemove: () => void;
}

const OtherPhotoCard: React.FC<OtherPhotoCardProps> = ({ photo, onRemove }) => {
    return (
        <div className="relative w-full aspect-square rounded-lg border border-gray-200 overflow-hidden">
            <img
                src={photo.preview || ''}
                alt="Other photo"
                className="w-full h-full object-cover"
            />
            <button
                onClick={onRemove}
                className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-gray-100"
            >
                <img src={xMark} alt="Remove" className="w-4 h-4" />
            </button>
        </div>
    );
};

export default UploadPatientPhotos;
